/*
This file is located in the main branch
Last time the file was updated (UTC): [19.08.2024 14:24:30]
Permissions to update outside the main branch: [true]
*/

import { boolPropStringToValue, PropExtended } from '@finbeat/react-toolkit';
import { BOPropValidatedValues } from 'base-components/BOPropValidatedValues/BOPropValidatedValues';
import { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Card, CardBody, CardHeader } from 'reactstrap';
import { RoleChangeModalFromType } from './RoleChangeModal';

const RolePropFeature = ({
    name,
    wrapper,
    reasonToDisplay,
}: {
    name: string;
    wrapper?: (propElement: JSX.Element) => JSX.Element;
    reasonToDisplay?: (prop: PropExtended) => boolean;
}) => {
    const { watch } = useFormContext<RoleChangeModalFromType>();
    const props = watch('props');
    const prop = props.find((i) => i.code === name);

    const item = prop && (
        <BOPropValidatedValues<RoleChangeModalFromType> name={`props.${props.indexOf(prop)}`} />
    );

    if (item && (!reasonToDisplay || reasonToDisplay(prop))) {
        return wrapper ? wrapper(item) : item;
    }

    return <></>;
};

const PropCard = ({ label, children }: { label: string } & React.PropsWithChildren) => {
    const { t } = useTranslation();
    return (
        <Card className="mt-3">
            <CardHeader>{t(label)}</CardHeader>
            <CardBody>{children}</CardBody>
        </Card>
    );
};

const RoleChangeModalTabAdditionalFeatures = () => {
    const wrapperStartPage = useCallback(
        (item: JSX.Element) => <PropCard label="BackOfficeUserStartPage"> {item} </PropCard>,
        []
    );

    const wrapperLeadgens = useCallback(
        (item: JSX.Element) => (
            <PropCard label="BackOfficeLeadgens">
                {item} <Leadgens />
            </PropCard>
        ),
        []
    );

    return (
        <>
            <RolePropFeature name="RoleCanViewAllOrders" />
            <RolePropFeature name="AllowOrderManualProcess" />
            <RolePropFeature name="CanViewClientPersonalData" />
            <hr />
            <PropCard label="BackOfficeCanEditTarriff">
                <RolePropFeature name="EditingAccess" />
                <BillServices />
            </PropCard>
            <RolePropFeature name="UserStartPage" wrapper={wrapperStartPage} />
            <RolePropFeature name="CanViewAllLeadgens" wrapper={wrapperLeadgens} />
            <RolePropFeature name="TradeOrgBindable" />
            <RolePropFeature name="BrandCodeBindable" />
            <RolePropFeature name="RoleCanViewOrdersWithActiveProlongationAgreements" />
            <RolePropFeature name="IsTradeOrgOwner" />
            <RolePropFeature name="ShowMerchantInstruction" />
        </>
    );
};

const BillServices = () => {
    const { watch } = useFormContext<RoleChangeModalFromType>();
    const props = watch('props');

    const partialAccess =
        props.find((i) => i.code === 'EditingAccess')?.values?.[0]?.value === 'PartialAccess';

    return <RolePropFeature name="BillServices" reasonToDisplay={() => partialAccess} />;
};

const Leadgens = () => {
    const { watch } = useFormContext<RoleChangeModalFromType>();
    const props = watch('props');
    const canViewAllLeadgens =
        props.find((i) => i.code === 'CanViewAllLeadgens')?.values?.[0]?.value ?? null;

    return (
        <RolePropFeature
            name="Leadgens"
            reasonToDisplay={() => !boolPropStringToValue(canViewAllLeadgens)}
        />
    );
};

export default RoleChangeModalTabAdditionalFeatures;
